import { portalAdmin } from './portalAdmin';

/**
 * Agent Deploy service (FEAT-206). The portal-admin Lambda invokes the
 * deployer-account numa-arcanum-agent-deployer Lambda for the browser, which
 * holds no invoke right on it (TASK-629 item 4). The deployer checks the
 * person's sign-in again and records them as the actor.
 */

export interface AgentDeployOutcome {
  libraryAgentId: string;
  agentId: string;
  title: string;
  status: 'upserted' | 'removed' | 'failed';
  error?: string;
}

export interface ClientDeployResult {
  clientName: string;
  status: 'succeeded' | 'partial' | 'failed' | 'skipped';
  upserted: AgentDeployOutcome[];
  removed: AgentDeployOutcome[];
  error?: string;
  deploymentId?: string;
  /** Whether this client's Numa enforces the managed-lock (has FEAT-206 code). */
  enforcementPresent?: boolean;
}

export interface PreviewResult {
  clientName: string;
  /** Arcanum-managed agents currently on the client. */
  current: { libraryAgentId: string; agentId: string; title: string }[];
  toAdd: { libraryAgentId: string; title: string }[];
  toUpdate: { libraryAgentId: string; title: string }[];
  toRemove: { libraryAgentId: string; agentId: string; title: string }[];
  /** Whether this client's Numa enforces the managed-lock (has FEAT-206 code). */
  enforcementPresent: boolean;
}

export interface DeploymentRecord {
  deployment_id: string;
  client_name: string;
  status: string;
  actor?: string;
  created_at: number;
  finished_at?: number;
  upserted?: AgentDeployOutcome[];
  removed?: AgentDeployOutcome[];
  error?: string;
}

export const agentDeployService = {
  /** Dry-run: compute the upsert/remove diff without writing. */
  async preview(clientName: string, selection?: { includeAll?: boolean; agentIds?: string[] }): Promise<PreviewResult> {
    return portalAdmin<PreviewResult>('agentDeploy.preview', { clientName, ...selection });
  },

  /** Deploy/update agents into a single client (uses its saved target unless overridden). */
  async deployToClient(
    clientName: string,
    selection?: { includeAll?: boolean; agentIds?: string[] }
  ): Promise<ClientDeployResult> {
    return portalAdmin<ClientDeployResult>('agentDeploy.deployToClient', { clientName, ...selection });
  },

  /** Deploy across every client that has a deploy target configured. */
  async deployFleet(): Promise<{ results: ClientDeployResult[] }> {
    return portalAdmin<{ results: ClientDeployResult[] }>('agentDeploy.deployFleet');
  },

  /** Recent deployment records for a client. */
  async statusForClient(clientName: string, limit = 10): Promise<DeploymentRecord[]> {
    const res = await portalAdmin<{ deployments: DeploymentRecord[] }>('agentDeploy.status', { clientName, limit });
    return res.deployments ?? [];
  },
};
